import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
} from '@mui/material';
import { tokens } from '../../Common/designTokens';
import { useUserConfigFormStore } from '../../Store/userConfigFormStore';
import { inputStyle } from '../OrderEntry/styles';
import { useAuth } from '../../Context/AuthProvider';
import { fetchVenues, fetchUserInfo } from '../../http/api';

const UserConfigForm = ({ mode = 'add', initialData = null }) => {
  const { token } = useAuth();
  const { formData, setFormData, fieldErrors, clearFieldError } = useUserConfigFormStore();

  // State for dropdown options
  const [venueOptions, setVenueOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      if (!token) return;

      try {
        // Fetch all dropdown data in parallel
        const [venuesResponse, usersResponse] = await Promise.all([
          fetchVenues(token, 0, 100, "venueMstId", "ASC"),
          fetchUserInfo(token, 0, 100, "userId", "ASC"),
        ]);

        // Process venues
        if (venuesResponse?.success && venuesResponse?.data?.content) {
          setVenueOptions(
            venuesResponse.data.content.map((venue) => ({
              id: venue.venueId,
              name: venue.venueName,
            }))
          );
        }

        // Process users
        if (usersResponse?.success && usersResponse?.data?.content) {
          setUserOptions(
            usersResponse.data.content.map((user) => ({
              id: user.userId,
              name: user.userName,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchDropdownData();
  }, [token]);

  // Load initial data in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        fixUserConfigMappingId: initialData.fixUserConfigMappingId,
        venueId: initialData.venueId || '',
        userId: initialData.userId || '',
        venueUser: initialData.venueUser || '',
      });
    }
  }, [mode, initialData, setFormData]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ [field]: value });
    clearFieldError(field);
  };

  return (
    <Box sx={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(95vh - 200px)' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
        {/* User Dropdown */}
        <Box>
          <FormControl fullWidth>
            <TextField
              select
              variant="filled"
              label="User Name"
              value={formData.userId}
              onChange={handleChange('userId')}
              sx={inputStyle}
              error={fieldErrors.userId}
              InputProps={{
                disableUnderline: true,
              }}
            >
              <MenuItem value="">
                <em>Select User</em>
              </MenuItem>
              {userOptions.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>

        {/* Venue Dropdown */}
        <Box>
          <FormControl fullWidth>
            <TextField
              select
              variant="filled"
              label="Venue Name"
              value={formData.venueId}
              onChange={handleChange('venueId')}
              sx={inputStyle}
              error={fieldErrors.venueId}
              InputProps={{
                disableUnderline: true,
              }}
            >
              <MenuItem value="">
                <em>Select Venue</em>
              </MenuItem>
              {venueOptions.map((venue) => (
                <MenuItem key={venue.id} value={venue.id}>
                  {venue.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>

        {/* Venue User */}
        <Box>
          <TextField
            fullWidth
            variant="filled"
            label="Venue User"
            value={formData.venueUser}
            onChange={handleChange('venueUser')}
            placeholder="Enter Venue User"
            inputProps={{ maxLength: 50 }}
            sx={inputStyle}
            error={
              fieldErrors.venueUser ||
              (formData.venueUser && formData.venueUser.length > 50)
            }
            helperText={
              formData.venueUser && formData.venueUser.length > 50
                ? "Maximum 50 characters allowed"
                : ""
            }
            InputProps={{
              disableUnderline: true,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default UserConfigForm;
